import { ReactNode } from 'react';

import { twMerge } from 'tailwind-merge';

interface BoxProps {
  readonly index: number;
  readonly date?: Date;
  readonly children: ReactNode;
  readonly disabled?: boolean;
  readonly inactive?: boolean;
  readonly active?: true | '' | 'first' | 'last' | 'firstlast';
  readonly onClick?: (date: Date) => void;
  readonly className?: string;
}

function getTextColor({ index, disabled, active, inactive }: Pick<BoxProps, 'index' | 'disabled' | 'active' | 'inactive'>) {
  if (active && typeof active === 'string') {
    return 'text-white';
  }

  if (disabled || (inactive && active !== true) || (active === true && (index === 0 || index === 6))) {
    return 'text-neutral-400';
  }

  return 'text-neutral-900';
}

function getBgColor({ active }: Pick<BoxProps, 'active'>) {
  if (active) {
    return 'bg-light-blue-50';
  }

  return undefined;
}

function shouldPush(dir: 'left' | 'right', isActive: boolean) {
  return isActive ? (dir === 'left' && 'pl-0 ml-1') || 'pr-0 mr-1' : undefined;
}

function Box({ index, children, className, active, disabled, inactive, date, onClick }: BoxProps) {
  const spanClasses = twMerge(
    'flex items-center justify-center',
    'px-1',
    shouldPush('left', (typeof active === 'string' && !!active.match('first')) || (active === true && index === 0)),
    shouldPush('right', (typeof active === 'string' && !!active.match('last')) || (active === true && index === 6)),
    active === true && index === 0 && 'rounded-l',
    active === true && index === 6 && 'rounded-r',
    getBgColor({ active }),
    className,
  );

  const classes = twMerge(
    'h-8 w-8',
    'text-base leading-8',
    'outline-none ring-neutral-200 ring-offset-1 focus:ring',
    'duration-300 ease-in-out',
    !(disabled || active) && 'hover:bg-neutral-100 hover:text-neutral-900',
    active === true && 'hover:text-blue-500',
    active && typeof active === 'string' && 'rounded-md bg-blue-500',
    getTextColor({ index, active, disabled, inactive }),
    'font-medium',
    disabled && 'pointer-events-none cursor-not-allowed',
  );

  return (
    <span className={spanClasses}>
      <button
        type="button"
        aria-label={date?.toDateString()}
        data-selected={!!active}
        disabled={disabled}
        onClick={date && !disabled ? () => onClick?.(date) : undefined}
        className={classes}
      >
        {children}
      </button>
    </span>
  );
}

export { Box };
